import React from 'react'
import { Link } from 'react-router-dom'
import { Box, ListItemButton, Typography, alpha } from '@mui/material'
import { useSidebarControls } from 'src/designSystem/Container/sidebarControls'
import UidShorthand from 'src/legacy/components/UidShorthand'
import PriorityIndicators from 'src/participantsHome/ParticipantListItem/PriorityIndicators'
import { useDraftMessage } from 'src/participantsHome/ParticipantListItem/useDraftMessage'
import { useStalledAfterDays } from '../api/AdvancedSettingsProvider'
import { AdvancedPatientSummary } from '../api/types'
import { isTrackStalled, quietFrom } from '../api/selectors'
import { wholeDaysSince } from '../format'
import StalledTrackIndicator from '../dashboard/identifiers/StalledTrackIndicator'
import { caseloadInk, onDark, surfaces } from '../theme'
import { rowChipsSx, rowViewSx } from './caseloadChrome'
import { renderCaseloadIndicators } from './caseloadIndicators'
import { CaseloadView, showsStalledTrack } from './caseloadView'
import { CaseloadParticipant } from './useAdvancedCaseloadParticipants'
import { useTrackCatalog } from '../api/useTrackCatalog'
import { trackTitle } from '../dashboard/tracks/trackTransitions'

type Props = {
  summary: AdvancedPatientSummary
  participant?: CaseloadParticipant
  view: CaseloadView
  selected: boolean
}

const AdvancedCaseloadListItem = ({ summary, participant, view, selected }: Props) => {
  const catalog = useTrackCatalog()
  const stalledAfterDays = useStalledAfterDays()
  const stalledTracks = summary.tracks.flatMap((track) => {
    const ownerRole = catalog.find((definition) => definition.trackKey === track.trackKey)?.ownerRole ?? null
    const days = isTrackStalled(track, stalledAfterDays) ? wholeDaysSince(quietFrom(track)) : null

    return days !== null && showsStalledTrack(view, ownerRole) ? [{ trackKey: track.trackKey, days }] : []
  })
  const { isExpanded } = useSidebarControls()
  const draftMessage = useDraftMessage(summary.uid)
  const ink = selected ? caseloadInk.selected : caseloadInk.default

  return (
    <ListItemButton
      component={Link}
      to={`/advanced/${summary.uid}`}
      selected={selected}
      sx={{
        display: 'block',
        px: 2,
        py: 1.5,
        borderBottom: `1px solid ${alpha(onDark.primary, 0.08)}`,
        '&:hover': { backgroundColor: alpha(onDark.primary, 0.05) },
        '&.Mui-selected, &.Mui-selected:hover': { backgroundColor: surfaces.green.bg },
      }}
    >
      {/* The name keeps its width; chips that don't fit after it continue on the next line, right-aligned. */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'flex-end', alignItems: 'center', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: '1 0 auto', maxWidth: '100%' }}>
          {renderCaseloadIndicators('name', view, { summary, participant, expanded: isExpanded })}
          <Typography
            variant="h5"
            sx={{
              minWidth: 0,
              color: ink.primary,
              fontWeight: selected ? 600 : undefined,
              wordBreak: 'break-word',
            }}
          >
            {participant?.displayName || <UidShorthand uid={summary.uid} />}
          </Typography>
        </Box>

        <Box sx={[{ display: 'contents' }, rowChipsSx(selected), rowViewSx(view)]}>
          {renderCaseloadIndicators('chips', view, { summary, participant, expanded: isExpanded })}
          {stalledTracks.map((track) => (
            <StalledTrackIndicator
              key={track.trackKey}
              title={trackTitle(catalog, track.trackKey)}
              days={track.days}
              expanded={isExpanded}
            />
          ))}
          {/* Dissolves Standard's chip cluster so its chips wrap one by one; `&&` outweighs its makeStyles class. */}
          <Box sx={{ display: 'contents', '&& > *': { display: 'contents' } }}>
            <PriorityIndicators
              expanded={isExpanded}
              draftMessage={draftMessage}
              indicators={participant?.indicators}
              tags={participant?.tags}
            />
          </Box>
        </Box>
      </Box>
    </ListItemButton>
  )
}

export default AdvancedCaseloadListItem
